import React, { useState, useEffect, useMemo } from "react";

const MEDICINE_STATUS_BADGE = {
    "Authorised": "discovery-badge-green",
    "Withdrawn":  "discovery-badge-orange",
    "Refused":    "discovery-badge-red",
    "Suspended":  "discovery-badge-orange",
    "Revoked":    "discovery-badge-orange",
};

// ── Click-to-open text filter — same pattern as "Source Type" filter in LiteratureSearchesList ──
const TextFilter = ({ label, options, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selectedLabel = value === "ALL" ? label : value;

    return (
        <div className="position-relative" ref={ref}>
            <div
                className="d-flex align-items-center gap-1 cursor-pointer"
                onClick={() => setOpen((p) => !p)}
            >
                <label className="cursor-pointer ev-small-p-blue ev-bold-med mb-0">{selectedLabel}</label>
                <i className="bi bi-funnel-fill ev-fs-11 ev-small-p-blue"></i>
            </div>
            {open && (
                <div className="ev-card-8 position-absolute top-100 end-0 mt-1 p-2 ev-radius-xsmall d-flex flex-column scrollable-element" style={{ zIndex: 9999, minWidth: "200px", maxHeight: "260px" }}>
                    {options.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            className={`w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ${
                                value === opt
                                    ? "ev-btn-blue-fill-round"
                                    : "ev-btn-light-fill ev-reg-p-blue"
                            }`}
                            onClick={() => {
                                onChange(opt);
                                setOpen(false);
                            }}
                        >
                            {opt === "ALL" ? `All ${label}` : opt}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const MedicineRow = ({ medicine, isActive, isChecked, onSelect, onCheck }) => {
    const docCount = (medicine.documents || []).filter((d) => d.document_url).length;
    const statusBadgeClass = MEDICINE_STATUS_BADGE[medicine.medicine_status] || "discovery-badge-neutral";

    return (
        <tr
            className={`cursor-pointer ${isActive ? "discovery-row-active" : ""}`}
            onClick={() => onSelect?.(medicine)}
        >
            <td className="align-top w-55" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                <div className="d-flex align-items-start gap-2">
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                            e.stopPropagation();
                            onCheck?.(medicine.ema_medicine_id, e.target.checked);
                        }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <p className="ev-reg-p-dark ev-bold-med mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                {medicine.name_of_medicine || "—"}
                            </p>
                            {medicine.orphan && <span className="ev-badge-pill ev-fs-11 ev-badge-purple">Orphan</span>}
                            {medicine.biosimilar && <span className="ev-badge-pill ev-fs-11 ev-badge-blue">Biosimilar</span>}
                            {medicine.generic && <span className="ev-badge-pill ev-fs-11 ev-badge-light">Generic</span>}
                        </div>
                        <p className="ev-small-p-dark mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {medicine.active_substance || "—"}
                            {medicine.inn ? ` · INN: ${medicine.inn}` : ""}
                        </p>
                        <p className="ev-small-p-dark mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {medicine.marketing_authorisation_holder || "—"}
                        </p>
                    </div>
                </div>
            </td>

            <td className="align-top w-35 text-end">
                <div className="d-flex flex-column align-items-end gap-1">
                    {docCount > 0 ? (
                        <span className="discovery-badge-green">
                            <i className="bi bi-file-earmark-pdf me-1"></i>
                            {docCount} document{docCount > 1 ? "s" : ""}
                        </span>
                    ) : (
                        <span className="discovery-badge-unknown" style={{ width: "auto", border: "1px solid var(--color8)" }}>
                            No documents
                        </span>
                    )}
                    {medicine.medicine_status && (
                        <span className={statusBadgeClass} style={{ width: "auto" }}>
                            {medicine.medicine_status}
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );
};

const EMAResultsList = ({
    medicines = [],
    selectedResult,
    onSelectResult,
    selectedIds = [],
    selectAll = false,
    onSelectAll,
    onSelectOne,
}) => {
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [areaFilter, setAreaFilter] = useState("ALL");
    const selectedCount = selectedIds.length;

    const statusOptions = useMemo(() => {
        const set = new Set(medicines.map((m) => m.medicine_status).filter(Boolean));
        return ["ALL", ...Array.from(set).sort()];
    }, [medicines]);

    const areaOptions = useMemo(() => {
        const set = new Set(medicines.map((m) => m.therapeutic_area_mesh).filter(Boolean));
        return ["ALL", ...Array.from(set).sort()];
    }, [medicines]);

    const filteredMedicines = useMemo(() => {
        return medicines.filter((m) => {
            if (statusFilter !== "ALL" && m.medicine_status !== statusFilter) return false;
            if (areaFilter !== "ALL" && m.therapeutic_area_mesh !== areaFilter) return false;
            if (showSelectedOnly && !selectedIds.includes(m.ema_medicine_id)) return false;
            return true;
        });
    }, [medicines, statusFilter, areaFilter, showSelectedOnly, selectedIds]);

    useEffect(() => {
        if (showSelectedOnly && selectedCount === 0) {
            setShowSelectedOnly(false);
        }
    }, [showSelectedOnly, selectedCount]);

    if (medicines.length === 0) {
        return (
            <div className="ev-card-2 p-4 vh-55 d-flex flex-column align-items-center justify-content-center text-center">
                <i className="bi bi-journal-x ev-fs-50 text-muted mb-3"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">No EMA Medicines Found</p>
                <p className="ev-small-p-blue mb-0">Run a search to fetch results.</p>
            </div>
        );
    }

    return (
        <div className="table-container" style={{ overflowX: "hidden" }}>

            <table className="table-custom1 mt-0 overflow-visible w-100 ev-message-card3 ev-radius-xsmall py-0 px-2">
                <thead>
                    <tr>
                        <th className="w-60 p-0">
                            <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => onSelectAll?.(e.target.checked)}
                                    />
                                    <label className="mb-0 text-nowrap">Medicines ({filteredMedicines.length})</label>
                                    <span className="ev-btn-white-fill-round ev-btn-xsmall ev-small-p-blue px-3 pe-1 ms-1 ev-bold-med text-nowrap">
                                        {selectedCount} of {medicines.length} Selected
                                        <button
                                            type="button"
                                            className={`ev-btn-xsmall ev-bold-med ev-radius-xlarge ${
                                                showSelectedOnly
                                                    ? "ev-btn-blue-fill-round ms-2"
                                                    : "ev-btn-skyblue-fill-round ev-small-p-blue ms-2"
                                            }`}
                                            onClick={() => setShowSelectedOnly((prev) => !prev)}
                                            disabled={!showSelectedOnly && selectedCount === 0}
                                        >
                                            {showSelectedOnly ? "Show All" : "Show Selected"}
                                        </button>
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-2 pe-2 flex-shrink-0">
                                    <TextFilter
                                        label="Statuses"
                                        options={statusOptions}
                                        value={statusFilter}
                                        onChange={setStatusFilter}
                                    />
                                    <TextFilter
                                        label="Areas"
                                        options={areaOptions}
                                        value={areaFilter}
                                        onChange={setAreaFilter}
                                    />
                                </div>
                            </div>
                        </th>
                    </tr>
                </thead>
            </table>

            <div className="scrollable-element vh-50 pe-1" style={{ overflowX: "hidden" }}>
                <table className="table-custom1 overflow-hidden w-100" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                        <col style={{ width: "65%" }} />
                        <col style={{ width: "35%" }} />
                    </colgroup>
                    <tbody>
                        {filteredMedicines.length === 0 ? (
                            <tr>
                                <td colSpan="2" className="text-center py-5">
                                    <i className="bi bi-funnel ev-fs-50 text-muted mb-3 d-block"></i>
                                    <p className="ev-reg-p-dark ev-bold-med mb-1">No Matching Medicines</p>
                                    <p className="ev-small-p-dark text-muted mb-0">
                                        Try adjusting the filters above.
                                    </p>
                                </td>
                            </tr>
                        ) : (
                            filteredMedicines.map((medicine) => (
                                <MedicineRow
                                    key={medicine.ema_medicine_id}
                                    medicine={medicine}
                                    isActive={selectedResult?.ema_medicine_id === medicine.ema_medicine_id}
                                    isChecked={selectedIds.includes(medicine.ema_medicine_id)}
                                    onSelect={onSelectResult}
                                    onCheck={onSelectOne}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EMAResultsList;